/**
 * 加盟店側-ユーザー検索フォーム
 * メモ：検索フォームを表示する
 */
import React from "react";

import clsx from "clsx";
import {
  FormControl,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  makeStyles,
} from "@material-ui/core";

import { formStyles } from "../../../../common/components/styles";

import { GuestSearchCondition } from "../../types";

import { FONT, GUEST_COLOR } from "../../../../../constants/common";
import {
  USER_TYPE_SEARCH_MAP_FOR_CUSTOMER,
  USER_TYPE_LIST,
} from "../../../../../constants/user";

import { getUserType } from "../../../../../api/utils/authUtils";
import { isSysAd } from "../../../../../utils/auth";

import CustomTextField from "../../../../common/components/atoms/CustomTextField";
import CustomSelect from "../../../../common/components/molecules/CustomSelect";
import PatternMatchChip from "../../../../common/components/atoms/PatternMatchChip";

// レイアウト
const useStyles = makeStyles(() => ({
  label: {
    width: 800,
    textAlign: "left",
    padding: "0!important",
    height: 36,
  },
  attention: {
    fontSize: "0.7rem",
    textAlign: "left",
    fontFamily: FONT,
    marginTop: 0,
  },
  table: {
    width: 800,
    minWidth: 800,
  },
  td: {
    padding: 1,
    backgroundColor: "#fff",
    color: "#000!important",
    borderTop: "1px solid #d2d5d8",
    borderBottom: "1px solid #d2d5d8",
    borderRight: "1px solid #d2d5d8",
    width: 400,
  },
  th: {
    padding: 0,
    fontFamily: FONT,
    backgroundColor: "#fff",
    color: "#000!important",
    width: 200,
    border: "1px solid #d2d5d8",
    position: "relative",
    paddingLeft: 20,
  },
}));

// 検索条件フォーム設定
const userSearchFormData = [
  {
    name: "会社名",
    formData: "contractorName",
    matchType: "部分一致",
  },
  {
    name: "所属加盟店",
    formData: "customerName",
    matchType: "部分一致",
  },
  {
    name: "ユーザー名",
    formData: "name",
    matchType: "前方一致",
  },
  {
    name: "ユーザーID",
    formData: "userName",
    matchType: "前方一致",
  },
  {
    name: "メールアドレス",
    formData: "email",
    matchType: "前方一致",
  },
  {
    name: "ユーザー種別",
    formData: "userType",
    matchType: "完全一致",
  },
];

// 加盟店ユーザー検索のインターフェース
interface Props {
  searchCondition: GuestSearchCondition;
  setSearchCondition: (value: GuestSearchCondition) => void;
}

// フォーム表示処理
const GuestUserSearchConditionForm: React.FC<Props> = (props: Props) => {
  const classes = useStyles();
  const formClasses = formStyles({
    color: GUEST_COLOR.base,
    backgroundColor: GUEST_COLOR.primary,
  });

  const { searchCondition, setSearchCondition } = props;
  const userType = getUserType();

  // ユーザー種別のプルダウン用リスト
  const userTypeList = Object.keys(USER_TYPE_SEARCH_MAP_FOR_CUSTOMER).filter(
    (typeName) => {
      if (
        isSysAd() ||
        userType === USER_TYPE_LIST.SUPER_REPRESENTATIVE_MANAGER ||
        userType === USER_TYPE_LIST.REPRESENTATIVE_MANAGER
      ) {
        // システム、本社スーパー、本社管理者の場合は全タイプ検索可能（「指定なし」も含む）
        return true;
      }
      // 自身と同じか自身より下の権限のユーザーを検索可能（「指定なし」も含む）
      return (
        USER_TYPE_SEARCH_MAP_FOR_CUSTOMER[typeName] === "" ||
        USER_TYPE_SEARCH_MAP_FOR_CUSTOMER[typeName] >= (userType as string)
      );
    }
  );

  // システム担当者以外は会社名の選択肢を省く
  const userSearchForm = isSysAd()
    ? userSearchFormData
    : userSearchFormData.filter((item) => item.formData !== "contractorName");

  return (
    <FormControl className={formClasses.form}>
      <FormLabel className={clsx(formClasses.formLabel, classes.label)}>
        ユーザー検索
      </FormLabel>
      <TableContainer>
        <Table
          className={clsx(formClasses.table, classes.table)}
          aria-label="simple table"
        >
          <TableBody>
            {userSearchForm.map((formItem) => (
              <TableRow key={formItem.name}>
                <TableCell
                  align="left"
                  className={clsx(formClasses.th, classes.th)}
                >
                  {formItem.name}
                  <PatternMatchChip
                    left={153}
                    top="24%"
                    text={formItem.matchType}
                  />
                </TableCell>
                <TableCell align="left" className={classes.td}>
                  {/* ユーザー分類以外の場合 */}
                  {formItem.formData !== "userType" && (
                    <CustomTextField
                      value={
                        searchCondition[
                          formItem.formData as keyof GuestSearchCondition
                        ]
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        searchCondition[
                          formItem.formData as keyof GuestSearchCondition
                        ] = e.target.value;
                        setSearchCondition(searchCondition);
                      }}
                    />
                  )}
                  {/* ユーザー分類の場合 */}
                  {formItem.formData === "userType" && (
                    <CustomSelect
                      value={
                        searchCondition[
                          formItem.formData as keyof GuestSearchCondition
                        ] || "指定なし"
                      }
                      pulldownArray={userTypeList}
                      width="100%"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        searchCondition[
                          formItem.formData as keyof GuestSearchCondition
                        ] = e.target.value;
                        setSearchCondition(searchCondition);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </FormControl>
  );
};

export default GuestUserSearchConditionForm;
